import { useIonRouter } from '@ionic/react';
import { formatPostDateTime } from '../utils/postDate';

interface PostMetaProps {
  channelName: string;
  channelId?: string;
  authorName?: string | null;
  publishedAt: string;
  editedAt?: string | null;
  /** `sm` in the timeline, `lg` on the post detail screen. */
  size?: 'sm' | 'lg';
}

// Header block shared by the timeline row and the post detail screen. The
// channel name is the headline (the identity the user opted into); the author
// and date form a secondary, muted meta line below it. When `editedAt` is set,
// a muted "· (Editado <hora>)" marker is appended after the publish date.
//
// In the timeline (`sm`) the channel name reuses the Equipo role-header style
// (compact uppercase label) and the meta line stays on a single truncated line,
// so a one-line post takes little vertical space. On the detail screen (`lg`)
// the name is a prominent title.
//
// When `channelId` is provided the channel name becomes an in-app link to the
// channel page (X-style: same text colour, no underline). It stops propagation
// so tapping it does not also trigger the wrapping PostCard navigation.
export const PostMeta = ({ channelName, channelId, authorName, publishedAt, editedAt, size = 'sm' }: PostMetaProps) => {
  const ionRouter = useIonRouter();

  const openChannel = (event: React.MouseEvent) => {
    event.preventDefault(); // we do SPA navigation, not a full page load
    event.stopPropagation(); // keep the wrapping PostCard from also navigating
    ionRouter.push(`/app/main/channels/${channelId}`);
  };

  const isLg = size === 'lg';

  // Sizing/weight/margins are set inline on purpose: Ionic ships unlayered
  // global `h2` typography (font-size, font-weight, margin-bottom) that beats
  // Tailwind's layered utilities, which otherwise makes the channel name huge
  // and adds a large gap before the author line.
  return (
    <div className="min-w-0">
      <h2
        className="truncate"
        style={{
          margin: 0,
          fontFamily: 'var(--app-font-heading)',
          fontWeight: 700,
          fontSize: isLg ? '18px' : '15px',
          lineHeight: 1.15,
          color: '#141b2b',
        }}
      >
        {channelId ? (
          <a
            href={`/app/main/channels/${channelId}`}
            className="cursor-pointer"
            style={{ color: 'inherit', textDecoration: 'none' }} // X-style: same colour, no underline
            onClick={openChannel}
          >
            {channelName}
          </a>
        ) : (
          channelName
        )}
      </h2>
      <div
        className={isLg ? 'break-words' : 'truncate'}
        style={{
          marginTop: '1px',
          fontSize: isLg ? '14px' : '12.5px',
          lineHeight: 1.3,
          color: '#8a93a3',
        }}
      >
        {authorName ? `${authorName} · ` : ''}
        {formatPostDateTime(publishedAt)}
        {editedAt ? ` · (Editado ${formatPostDateTime(editedAt)})` : ''}
      </div>
    </div>
  );
};
